const AUTH_LINK_STATE_KEY = 'sugarmAuthLink'
const AUTH_LINK_PATHS = ['/auth/reset-password', '/auth/verify-email', '/auth/verify-email-result']
const AUTH_LINK_KEYS = ['token', 'email', 'user_id'] as const

type AuthLinkParameters = Partial<Record<(typeof AUTH_LINK_KEYS)[number], string>>
type AuthLinkEntry = {
  position: number
  path: string
  parameters: AuthLinkParameters
}
type AuthLinkRoute = {
  path: string
  query: Record<string, string | null | (string | null)[]>
  hash: string
}

const readParameters = (query: AuthLinkRoute['query'], hash: string): AuthLinkParameters => {
  const fragment = new URLSearchParams(hash.replace(/^#\??/, ''))
  return AUTH_LINK_KEYS.reduce<AuthLinkParameters>((parameters, key) => {
    const queryValue = query[key]
    const value = (Array.isArray(queryValue) ? queryValue.find(item => typeof item === 'string') : queryValue)
      ?? fragment.get(key)
    if (typeof value === 'string') parameters[key] = value
    return parameters
  }, {})
}

export const getSafeAuthNavigation = (route: AuthLinkRoute) => {
  if (!AUTH_LINK_PATHS.includes(route.path.replace(/\/+$/, ''))) return
  const parameters = readParameters(route.query, route.hash)
  if (Object.keys(parameters).length === 0) return

  const query = { ...route.query }
  AUTH_LINK_KEYS.forEach(key => delete query[key])
  return {
    path: route.path,
    query,
    hash: '',
    state: { [AUTH_LINK_STATE_KEY]: { path: route.path, parameters } }
  }
}

// Run before router creation or analytics loading. history.state keeps a direct
// email link usable on refresh without putting credentials in web storage/URLs.
export const protectInitialAuthLink = () => {
  if (typeof window === 'undefined') return
  const url = new URL(window.location.href)
  const safeRoute = getSafeAuthNavigation({
    path: url.pathname,
    query: Object.fromEntries(url.searchParams),
    hash: url.hash
  })
  if (!safeRoute) return

  AUTH_LINK_KEYS.forEach(key => url.searchParams.delete(key))
  url.hash = ''
  window.history.replaceState({ ...window.history.state, ...safeRoute.state }, '', url.href)
}

export const readAuthLinkParameters = (): AuthLinkParameters => {
  if (typeof window === 'undefined') return {}
  const stored = window.history.state?.[AUTH_LINK_STATE_KEY]
  return stored?.path === window.location.pathname ? { ...stored.parameters } : {}
}

export const captureAuthLinkEntry = (token: string): AuthLinkEntry | undefined => {
  if (typeof window === 'undefined') return
  const position = window.history.state?.position
  const parameters = readAuthLinkParameters()
  if (typeof position !== 'number' || !token || parameters.token !== token) return
  return { position, path: window.location.pathname, parameters }
}

export const clearAuthLinkParameters = (entry: AuthLinkEntry | undefined): boolean => {
  if (!entry) return false
  const current = captureAuthLinkEntry(entry.parameters.token || '')
  if (!current || current.position !== entry.position || current.path !== entry.path ||
    AUTH_LINK_KEYS.some(key => current.parameters[key] !== entry.parameters[key])) return false

  // History can replace only the current entry. A late response must not erase
  // another entry's credential; its original token is still single-use server-side.
  const state = { ...window.history.state }
  delete state[AUTH_LINK_STATE_KEY]
  window.history.replaceState(state, '')
  return true
}
